import styled from 'styled-components';
import {
  learningStreakText,
  learnLevelName,
  learnSessionScoreMax,
  learnUnlockScore,
  learnUnlockSessions,
  nextLearnLevel,
} from '../../../../common/src/learn_rules.ts';
import { LearnLevel, LearnModuleProgress } from '../../../../common/src/model.ts';
import { ButtonColumn, PrimaryButton, SecondaryButton } from './learn_buttons.tsx';
import { resultColorCss, SquareColor } from './learn_colors.ts';
import { ScreenBody, Slack } from './learn_screen_parts.tsx';

export type { SquareColor };

// A row an exercise adds to the card, between the best score and what unlocks the next level
// ("Trickiest today", issue #3721)
export interface SummaryCardRow {
  label: string;
  value: string;
}

// The ids and test ids this screen renders for one exercise's prefix ('rhythm', 'pitch'), so the
// exercise that mounts it and the stories that drive it name the same strings (issue #3518).
export function summaryScreenIds(prefix: string) {
  return {
    screen: `${prefix}-summary`,
    score: `${prefix}-summary-score`,
    square: `${prefix}-summary-square`,
    unlocked: `${prefix}-summary-unlocked`,
    squaresCaption: `${prefix}-summary-squares-caption`,
    extraRow: `${prefix}-summary-extra-row`,
    playAgain: `${prefix}-play-again-button`,
    backToLearn: `${prefix}-back-to-learn-button`,
  };
}

// The session summary every Learn exercise ends on (issues #3473, #3518), from the "Session
// summary" artboard: the score out of 10, the best run of hits in a row ("run", so it never reads
// as either streak - issue #3585), one square per attempt in its result colour, then a card with
// the learning streak, the best score at this level (with "(new)" when this session set
// it) and what unlocks the next level, and Play again and the way back to the module's class
// screen ("Back to Pitch", issue #3719), whose label the exercise passes. Presentational: `module`
// is the member's module progress after the session was recorded, and the flags come from
// recordLearnSession. `idPrefix` is the exercise's own ('rhythm', 'pitch'), so each screen's ids
// stay its own. An exercise may add a line under the squares saying what their colours mean, and
// rows of its own to the card (Interval Training's "Trickiest today", issue #3721); without them
// the screen is the one Pitch Match and Rhythm Echo end on.
export const LearnSummaryScreen = ({
  idPrefix,
  moduleName,
  level,
  score,
  bestStreak,
  squares,
  squaresCaption,
  extraRows = [],
  module,
  dayStreak,
  isNewBest,
  unlockedLevel,
  onPlayAgain,
  backLabel,
  onBack,
}: {
  idPrefix: string;
  moduleName: string; // "Rhythm Echo", for the eyebrow
  level: LearnLevel;
  score: number;
  bestStreak: number;
  squares: SquareColor[]; // one per attempt, in order
  squaresCaption?: string; // what the squares' colours mean, under them
  extraRows?: SummaryCardRow[]; // after the best score, before what unlocks the next level
  module: LearnModuleProgress;
  dayStreak: number;
  isNewBest: boolean;
  unlockedLevel?: LearnLevel; // the level this session unlocked, if any
  onPlayAgain: () => void;
  backLabel: string; // "Back to Pitch"
  onBack: () => void;
}) => {
  const best = module.bestScore[level] ?? score;
  const next = nextLearnLevel(level);
  const ids = summaryScreenIds(idPrefix);
  return (
    <ScreenBody id={ids.screen}>
      <Eyebrow>{`${moduleName} · ${learnLevelName(level)}`}</Eyebrow>
      <Score>
        <ScoreValue data-testid={ids.score}>{score}</ScoreValue>
        <ScoreMax>/{learnSessionScoreMax}</ScoreMax>
      </Score>
      <Streak>Best run: {plural(bestStreak, 'in a row')}</Streak>
      <Squares>
        {/* an attempt's position in the session is its only identity, so it is the key */}
        {Array.from(squares, (color, index) => (
          <Square
            key={index}
            style={{ backgroundColor: resultColorCss[color] }}
            data-testid={ids.square}
            data-color={color}
          />
        ))}
      </Squares>
      {squaresCaption && <SquaresCaption data-testid={ids.squaresCaption}>{squaresCaption}</SquaresCaption>}
      {unlockedLevel && <Unlocked data-testid={ids.unlocked}>{learnLevelName(unlockedLevel)} unlocked!</Unlocked>}
      <Card>
        <CardRow>
          <span>Learning streak</span>
          <span>{learningStreakText(dayStreak)}</span>
        </CardRow>
        <CardRow>
          <span>Best score at {learnLevelName(level)}</span>
          <span>
            {best}/{learnSessionScoreMax}
            {isNewBest && <New> (new)</New>}
          </span>
        </CardRow>
        {extraRows.map((row) => (
          <CardRow key={row.label} data-testid={ids.extraRow}>
            <span>{row.label}</span>
            <span>{row.value}</span>
          </CardRow>
        ))}
        {next && (
          <CardRow>
            <span>
              {module.unlocked.includes(next)
                ? `${learnLevelName(next)} unlocked`
                : `${learnLevelName(next)} unlocks at`}
            </span>
            <span>{module.unlocked.includes(next) ? '' : unlockValue(module, level)}</span>
          </CardRow>
        )}
      </Card>
      <Slack />
      <ButtonColumn>
        <PrimaryButton id={ids.playAgain} data-testid={ids.playAgain} onClick={onPlayAgain}>
          Play again
        </PrimaryButton>
        <SecondaryButton id={ids.backToLearn} data-testid={ids.backToLearn} onClick={onBack}>
          {backLabel}
        </SecondaryButton>
      </ButtonColumn>
    </ScreenBody>
  );
};

// "3 sessions of 8+", with how many so far once there are any
function unlockValue(module: LearnModuleProgress, level: LearnLevel): string {
  const qualifying = module.qualifyingSessions[level] ?? 0;
  const rule = `${learnUnlockSessions} sessions of ${learnUnlockScore}+`;
  return qualifying > 0 ? `${rule} (${qualifying} so far)` : rule;
}

function plural(count: number, suffix: string): string {
  return `${count} ${suffix}`;
}

const shortScreen = '@media (max-height: 700px)';

const Eyebrow = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-darker);
`;

const Score = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-top: 8px;
`;

const ScoreValue = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 64px;
  line-height: 1;
  color: var(--color-tab-selected);

  ${shortScreen} {
    font-size: 52px;
  }
`;

const ScoreMax = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 28px;
  line-height: 1;
  color: var(--color-text-darker);
`;

const Streak = styled.div`
  margin-top: 12px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  line-height: 28px;
`;

const Squares = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

const Square = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
`;

const SquaresCaption = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;

const Unlocked = styled.div`
  margin-top: 14px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  line-height: 24px;
  color: var(--color-tab-selected);
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding: 16px;
  border-radius: 14px;
  background-color: var(--color-background-search);

  ${shortScreen} {
    margin-top: 14px;
    padding: 12px 16px;
    gap: 8px;
  }
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 15px;
  line-height: 22px;

  & > span:first-child {
    color: var(--color-text-darker);
  }

  & > span:last-child {
    font-family: Jost-SemiBold, Arial, sans-serif;
    text-align: right;
  }
`;

const New = styled.span`
  color: var(--color-tab-selected);
`;
